// The cosmic map: the page (V4CosmicMap), a brand's sky, and the project's sky
// (V4CielDossier, ciel-dossier.ts).
export default {
  titre: 'Cosmic map',
  pleinEcran: 'Full screen',
  aValider: 'to validate',
  dashboard: 'Dashboard',
  gererCluster: 'Manage the cluster',
  aucuneMarque: 'This project has no brands yet: there is nothing to place on the sky.',

  // ── The page and a brand's sky ────────────────────────────────────────────
  page: {
    sousTitreDossier: '{dossier} · its brands on a single sky, and what they share',
    leDossier: 'The project',
    sousTitreMarque: 'Audience overlaps · white space · positions by proximity, not by category',
    construction: 'Building the audience sky…',
    sansDossier: 'The cosmic map is read per project: choose one in the left sidebar. It places the project’s brands on a single sky, along with what they share.',
    mesDossiers: 'My projects',
    erreurMarques: 'The brand list could not be loaded: the map cannot tell what the project contains.',
    onglets: 'The project’s skies',
    ongletRecouvrementAide: 'The project’s brands on a single sky, and what they share',
    recouvrement: 'Overlap',
    cielDe: 'The sky of {nom}',
    lecture: 'Nodes = brands · edges = audience overlap calculated from the personas. Click a solar system to enter it, a node for details.',
    sansGalaxie: 'Select a galaxy with sub-brands to display the map.',
    couvertureAide: 'Estimated coverage of the territory by this brand (lexical, not an audience overlap)',
    revenirVueGalaxie: 'Back to the galaxy view',
    entrerSysteme: 'Enter this system',
    ouvrirBriefAide: 'Open this territory’s brief',
    espaceLibre: 'White space',
    cielAudience: 'Audience sky · {date}',
    recouvrementMax: 'Max overlap {n}% ·',
    force: 'strength {n}/100 ·',
    overlaps: 'ⓘ persona overlaps',
    vueEnsemble: 'Overview',
    entrerDansSysteme: 'Enter the system',
  },
  niveau: {
    galaxy: 'Galaxy',
    solar_system: 'Solar system',
    planet: 'Planet',
  },
  mode: {
    nebula: 'Nebula',
    audiences: 'Audiences',
    territories: 'Territories',
    exploded: 'Exploded',
  },
  legende: {
    galaxie: 'Galaxy',
    systeme: 'Solar system',
    planete: 'Planet',
    audience: 'Audience (persona)',
    territoire: 'Territory (segment)',
    espaceLibre: 'White space (map)',
    epaisseur: 'thickness = overlap',
  },
  entete: {
    hierarchie: '{nom}: the full hierarchy.',
    systeme: 'The {nom} system.',
    audiences: 'The measured audiences of {nom}.',
    territoires: 'The territories of {nom}.',
    espaceLibre: 'The “{label}” space is open.',
    ciel: 'The audience sky of {nom}.',
  },
  corps: {
    sourceOverlaps: 'Overlaps calculated on each brand’s most recent audiences',
    dansSonCiel: 'bodies in its sky',
    mentionne: 'of the {nom} audience mention it',
    estime: 'estimated audience overlap with {nom}',
    avec: 'overlap with {nom}',
    concurrent: 'Competitor',
    concurrentSub: 'cluster competitor · estimated score',
    scoreEstime: 'estimated score',
    score: 'score',
    concurrentInsight: 'Competitor identified in the {nom} cluster: not yet linked to an Orion brand, so no measurable overlap.',
    positionInsight: 'Position in the {nom} hierarchy. Overlaps calculated on the audiences.',
    estimation: 'estimate',
    overlapsPersonas: 'persona overlaps',
    concurrentAide: 'Score and overlap estimated from the competitor’s awareness, to be validated. An in-depth audit will measure the actual overlap.',
    ouvrirChaine: 'Open the dashboard',
    territoireKind: 'Territory · segment',
    territoireSub: 'market segment · detected at audit',
    corpsSurTerritoire: 'bodies on this territory',
    partDe: 'share of {nom}',
    occupePar: 'Occupied by: {liste}.',
    aucunCorps: 'No body in the sky attached: audit the brands to populate this territory.',
    territoiresAudit: 'territories (audit)',
    territoiresAide: 'Segments detected automatically at audit and shared between brands. The share is the territory’s weight in the brand’s measured identity.',
    gererTerritoires: 'Manage the territories',
    audienceKind: 'Audience · persona',
    persona: 'persona',
    partAudience: 'audience share',
    confiance: 'confidence',
    tension: 'Tension: {texte}',
    personasV2: 'personas V2',
    personaAideGalaxie: 'Persona measured on the galaxy’s latest audit: the share is the estimated size of the segment within the audience.',
    personaAide: 'Persona measured on the latest audit: the share is the estimated size of the segment within the audience.',
    parlerPersona: 'Talk to this persona',
    vousEtesIci: 'you are here',
    partMarque: 'brand share',
    corpsDessus: 'bodies in the sky on it',
    partTerritoire: 'Share of the “{nom}” territory in the measured identity of {marque}.',
    revenirGalaxie: 'Back to the galaxy',
  },

  // ── The project's sky ─────────────────────────────────────────────────────
  ciel: {
    erreur: 'Could not load the project map',
    marque: 'Brand',
    territoire: 'Territory',
    concurrent: 'Competitor',
    persona: 'Persona',
    nb: {
      territoire: '{n} territories | {n} territory | {n} territories',
      concurrent: '{n} competitors | {n} competitor | {n} competitors',
      compte: '{n} accounts | {n} account | {n} accounts',
      dimension: '{n} dimensions | {n} dimension | {n} dimensions',
      marque: '{n} brands | {n} brand | {n} brands',
      persona: '{n} personas | {n} persona | {n} personas',
      sousMarque: '{n} sub-brands | {n} sub-brand | {n} sub-brands',
      signal: '{n} signals | {n} signal | {n} signals',
    },
    genre: {
      vertical: 'vertical',
      geographic: 'geographic',
    },
    relation: {
      direct: 'direct competitor',
      aspirational: 'aspirational model',
      autre: 'competitor',
    },
    rienMesurable: 'nothing measurable',
    rienEnCommun: 'nothing in common',
    manque: '{marque}: {raison}',
    separateur: '; ',
    et: 'and',
    etDe: 'and',
    titre: {
      partagent: '{a} and {b} share {liste}.',
      concept: '{a} and {b}: {concept}, across two markets.',
      memeCiel: '{a} and {b}, on the same sky.',
      toutes: 'The {n} brands of {dossier}, on the same sky.',
    },
    enCommunN: '({n} shared)',
    mesureAide: 'Communities: an estimate, calculated on each brand’s personas (same words, same platforms). Territories, competitors and accounts: measured on attachments, validated clusters and signals. A dimension that cannot be measured is stated as such, never counted as zero.',
    intro: 'The project’s brands on a single sky. What they share sits between them; the edge linking them carries what can be measured from one to the other. Click a body for details, a brand to open its sky.',
    construction: 'Building the project’s sky…',
    erreurConstruction: 'The project map could not be built: {erreur}',
    pairesAide: 'What can be measured between these two brands: click for details',
    memeVerticale: 'same vertical · {concept}',
    cielDuDossier: 'Project sky · {date}',
    commentMesure: 'ⓘ how it is measured',
    recouvrement: 'Overlap',
    dimensionsEnCommun: '{dimensions} in common',
    rienDeMesure: 'nothing measured in common',
    nonMesurables: '{n} not measurable | {n} not measurable | {n} not measurable',
    communautes: 'Communities',
    estimation: 'estimate',
    estimationAide: 'Calculated on the personas: a closeness of worlds, not a count of individuals',
    recouvrementPersonas: 'Overlap of their personas ({a} and {b}), the same formula as each brand’s map.',
    langues: 'Personas in {a} and in {b}: the comparison is based on words, so it underestimates the closeness.',
    faitLeChiffre: 'What drives the figure',
    mot: '“{mot}”',
    memesMots: 'same words: {mots}',
    memesPlateformes: 'same platforms: {liste}',
    seulementPlateformes: 'only the same platforms: {liste}',
    aucunMot: 'No word and no platform in common between their personas.',
    nonMesurable: 'Not measurable. {raisons}.',
    territoires: 'Territories',
    mesure: 'measured',
    territoiresCommun: 'In common, out of {a} and {b}:',
    aucunTerritoire: 'No territory in common (out of {a} and {b}).',
    verticaleDeuxMarches: 'Same vertical across two markets:',
    verticalePaire: '“{a}” and “{b}”.',
    memeUnivers: 'The same world, two populations: this is not an audience overlap.',
    concurrents: 'Competitors',
    concurrentsCommun: 'In common, out of {a} and {b} validated:',
    aucunConcurrent: 'No competitor in common (out of {a} and {b} validated).',
    cite: '{de} lists {vers} among its validated competitors.',
    comptesEnCommun: 'Accounts in common',
    memesComptes: 'The same accounts talking about both brands in separate content, out of {a} and {b} identifiable accounts.',
    signauxPaire: '{a} and {b} signals',
    createur: 'creator',
    etAutres: 'and {n} more.',
    memeContenu: '{comptes} in common through the same piece of content, collected for both brands ({liste}): it is the collection that overlaps, not the audience. Not counted.',
    echantillon: 'Read on each brand’s most recent signals: the count is a minimum.',
    cielDe: 'Sky of {nom}',
    marqueDuDossier: 'Project brand',
    marqueMin: 'brand',
    score: 'score',
    territoiresConcurrents: 'territories · competitors',
    personasJamais: 'Personas never calculated',
    comptesIdentifiables: '{signaux} from identifiable accounts',
    plusRecents: '(the most recent)',
    dansSonCiel: '{sousMarques} in its sky',
    sesRecouvrements: 'Its overlaps within the project',
    ouvrirSonCiel: 'Open its sky',
    principal: 'project’s main territory',
    ouvert: 'opened to the project',
    ouvertSansMarque: 'Opened to the project, held by none of its brands.',
    enCommunEntre: 'Shared between {liste}.',
    portePar: 'Held by {nom} only.',
    porteSous: '{nom} holds it through its sub-brands.',
    porteSousDirect: '{nom} holds it through its sub-brands, and directly.',
    porteUne: '{nom} holds it through a sub-brand.',
    porteUneDirect: '{nom} holds it through a sub-brand, and directly.',
    jumelle: 'Same vertical as “{nom}”: the same world in another market, two distinct populations.',
    jumellePorteurs: 'Same vertical as “{nom}”, held by {porteurs}: the same world in another market, two distinct populations.',
    territoiresDossierAide: 'Detected at audit or set by hand on one of the project’s brands. The client sees the fieldwork of its territories, never their synthesis.',
    territoiresDossier: 'ⓘ project territories',
    suiviOrion: 'tracked in Orion',
    externe: 'external',
    concurrentDe: 'Competitor of {liste}: they share it.',
    valideDans: 'Validated in the {nom} cluster only.',
    recouvrementEstime: 'overlap estimated by the model: {n}%',
    pct: '{n}%',
    sousMarque: 'Sub-brand',
    dansLeCielDe: 'In the sky of {nom}: its overlaps are read on that brand’s map.',
    ouvrirLeCielDe: 'Open the sky of {nom}',
    leDossier: 'The project',
    marqueAMarque: 'Overlaps, brand by brand',
    uneSeule: 'A single brand: there is no overlap to measure.',
    legende: {
      enCommun: 'in common',
      entreDeux: 'what can be measured between two brands',
      verticale: 'same vertical, another market',
      mesureEstime: 'ⓘ measured, estimated',
    },
  },
}
